import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { Skeleton } from '../../../../components';
import { formatCredits } from '../../../../utils/credits';
import { formatCompact } from '../../../../utils/money';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { MOBILE_MQ } from '../../../../theme/tokens';
import { CreditsModal } from '../CreditsModal';
import { Root, Mark, Amount } from './CreditsChip.styles';

/* The subtle Lumio OUTLINE spark (the hollow gradient-stroke mark on Lumio suggestion buttons,
   e.g. Acquisition's "Increase daily budget"), for AI-credit references — quieter than the full
   orb. Gradient-stroked asset, so it renders as a plain <img> (no recolour). */
const LUMIO_SPARK_SRC = '/assets/acq/lumio-spark.svg';

/**
 * AI-credits balance pill for the header, sitting to the right of the wallet balance. Clicking
 * it opens the credits modal (what credits are for, and a top-up) rather than navigating away, so
 * the owner can refill from wherever they are.
 *
 * The balance is the owner-level AI_CREDITS wallet (`state.ownerWallet.credits`, in AIC). It shows the
 * real balance once the wallet list loads (0 for an owner who hasn't bought credits yet — the BE creates
 * the wallet on first purchase); `credits` is null only before that first load, when a skeleton shows.
 */
export const CreditsChip = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const wallet = useAppSelector((state) => state.ownerWallet ?? null);
  const [modalOpen, setModalOpen] = React.useState(false);
  // Phone header is tight: show a compact figure with no "credits" unit (e.g. "1.1K"); desktop spells it out.
  const isMobile = useMediaQuery(MOBILE_MQ);

  // Make sure the owner's wallets are loaded (the balance lives there). TopBar loads them too; this is
  // a no-op then, and re-runs after a session teardown resets the slice.
  React.useEffect(() => {
    if (wallet && !wallet.loaded && !wallet.loading) void dispatch(fetchOwnerWallets());
  }, [wallet, dispatch]);

  if (!wallet) return null;
  const credits = wallet.credits;
  // Errored before the wallet list ever loaded (no balance to trust): keep the header clean.
  if (wallet.error && credits === null) return null;

  const ready = wallet.loaded && credits !== null;
  // Not ready and not even loading (e.g. no auth yet) → nothing to show.
  if (!ready && !wallet.loading) return null;

  return (
    <>
      <Root type="button" onClick={() => setModalOpen(true)} aria-label={t('AI credits')} title={t('AI credits')}>
        <Mark src={LUMIO_SPARK_SRC} alt="" aria-hidden="true" />
        {ready ? (
          isMobile ? (
            // Data value (compact number), rendered as-is — no "credits" unit on the phone header.
            <Amount text={formatCompact(credits as number)} />
          ) : (
            <Amount
              text={credits === 1 ? '{{count}} credit' : '{{count}} credits'}
              placeholders={{ count: formatCredits(credits as number) }}
            />
          )
        ) : (
          <Skeleton width={52} height={12} radius={6} />
        )}
      </Root>
      {modalOpen ? <CreditsModal onClose={() => setModalOpen(false)} /> : null}
    </>
  );
};

export default CreditsChip;
